if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.collectionStickyWasVisible = false;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      isMobileQuickAdd() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      handleCollectionStickyOnOpen() {
        if (!this.isMobileQuickAdd()) return;

        const collectionSticky = document.querySelector('.collection-sticky');
        this.collectionStickyWasVisible = false;

        if (collectionSticky && collectionSticky.classList.contains('visible')) {
          collectionSticky.classList.remove('visible');
          this.collectionStickyWasVisible = true;
        }
      }

      handleCollectionStickyOnClose() {
        if (!this.isMobileQuickAdd()) return;

        if (this.collectionStickyWasVisible) {
          const collectionSticky = document.querySelector('.collection-sticky');

          if (collectionSticky) {
            collectionSticky.classList.add('visible');
          }

          this.collectionStickyWasVisible = false;
        }
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        this.modalContent.innerHTML = '';
        this.handleCollectionStickyOnClose();

        if (preventFocus) this.openedBy = null;

        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        const spinner = opener.querySelector('.loading__spinner');

        if (spinner) spinner.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (!productElement) return;

            this.preprocessHTML(productElement);

            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            /*
              IMPORTANTE:
              El contenido del quick add viene por fetch.
              Por eso el sticky del modal debe inicializarse después de insertar el HTML.
            */
            this.initializeQuickAddStickyCart();

            requestAnimationFrame(() => {
              this.initializeQuickAddStickyCart();
            });

            setTimeout(() => {
              this.initializeQuickAddStickyCart();
            }, 300);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            this.handleCollectionStickyOnOpen();
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            if (spinner) spinner.classList.add('hidden');
          });
      }

      initializeQuickAddStickyCart() {
        const root = this.modalContent;

        if (!root) return;

        /*
          Delegamos eventos una sola vez en el contenedor del modal.
          Esto permite actualizar el sticky cuando cambia la variante.
        */
        if (root.dataset.quickAddStickyDelegated !== 'true') {
          root.dataset.quickAddStickyDelegated = 'true';

          const refreshSticky = () => {
            setTimeout(() => this.initializeQuickAddStickyCart(), 80);
            setTimeout(() => this.initializeQuickAddStickyCart(), 300);
          };

          root.addEventListener('change', (event) => {
            const target = event.target;

            if (!(target instanceof Element)) return;

            if (
              target.closest('variant-selects') ||
              target.closest('variant-radios') ||
              target.closest('.product-form__input') ||
              target.closest('product-form') ||
              target.closest('.sticky-form')
            ) {
              refreshSticky();
            }
          });

          root.addEventListener('input', (event) => {
            const target = event.target;

            if (!(target instanceof Element)) return;

            if (
              target.closest('variant-selects') ||
              target.closest('variant-radios') ||
              target.closest('.product-form__input') ||
              target.closest('.sticky-form')
            ) {
              refreshSticky();
            }
          });

          root.addEventListener('click', (event) => {
            const target = event.target;

            if (!(target instanceof Element)) return;

            if (
              target.closest('variant-selects') ||
              target.closest('variant-radios') ||
              target.closest('.product-form__input') ||
              target.closest('.sticky-quantity-selector')
            ) {
              refreshSticky();
            }
          });

          document.addEventListener('variant:change', refreshSticky);
          document.addEventListener('variantChange', refreshSticky);
          document.addEventListener('shopify:section:load', refreshSticky);
        }

        const stickyForms = root.querySelectorAll('.sticky-form');

        if (!stickyForms.length) return;

        stickyForms.forEach((stickyForm) => {
          this.setupQuickAddStickyForm(root, stickyForm);
        });
      }

      setupQuickAddStickyForm(root, stickyForm) {
        const stickyAddBtn =
          stickyForm.querySelector('.sticky-add-btn') ||
          stickyForm.querySelector('#sticky-add-btn');

        const stickyVariantId = stickyForm.querySelector('input[name="id"]');

        const stickyQuantity =
          stickyForm.querySelector('.sticky-quantity-input') ||
          stickyForm.querySelector('input[name="quantity"]');

        const stickyIncrease =
          stickyForm.querySelector('.sticky-quantity-btn[id*="increase"]') ||
          stickyForm.querySelector('#sticky-increase');

        const stickyDecrease =
          stickyForm.querySelector('.sticky-quantity-btn[id*="decrease"]') ||
          stickyForm.querySelector('#sticky-decrease');

        if (!stickyAddBtn || !stickyVariantId) return;

        const variants = this.readQuickAddStickyVariants(root);

        const addToCartText =
          stickyAddBtn.dataset.addText ||
          stickyAddBtn.getAttribute('data-add-text') ||
          stickyAddBtn.textContent.trim() ||
          'Add to cart';

        const soldOutText =
          stickyAddBtn.dataset.soldOutText ||
          stickyAddBtn.getAttribute('data-sold-out-text') ||
          'Sold out';

        const getMainVariantInput = () => {
          const mainProductForm = this.getQuickAddMainProductForm(root);

          return (
            mainProductForm?.querySelector('input[name="id"]') ||
            root.querySelector('input.product-variant-id[name="id"]') ||
            root.querySelector('variant-selects input[name="id"]') ||
            root.querySelector('variant-radios input[name="id"]')
          );
        };

        const getCurrentVariant = () => {
          const mainVariantInput = getMainVariantInput();

          if (mainVariantInput && mainVariantInput.value) {
            stickyVariantId.value = mainVariantInput.value;
            stickyVariantId.setAttribute('value', mainVariantInput.value);
          }

          return (
            variants.find((variant) => String(variant.id) === String(stickyVariantId.value)) ||
            null
          );
        };

        const updateStickyState = () => {
          const variant = getCurrentVariant();

          let hasStock = this.quickAddVariantHasStock(variant);

          /*
            Si no existe el JSON de variantes, usamos el botón principal
            del formulario del quick add como fallback.
          */
          if (hasStock === null) {
            hasStock = this.quickAddFallbackHasStock(root);
          }

          const maxQty = this.getQuickAddVariantMaxQuantity(variant);

          let qty = 1;

          if (stickyQuantity) {
            qty = parseInt(stickyQuantity.value, 10) || 1;
            qty = Math.max(1, Math.min(qty, maxQty));

            stickyQuantity.value = String(qty);
            stickyQuantity.setAttribute('value', String(qty));
            stickyQuantity.max = String(maxQty);
            stickyQuantity.disabled = !hasStock;
          }

          const isBusy =
            stickyAddBtn.classList.contains('loading') ||
            stickyAddBtn.classList.contains('success');

          stickyAddBtn.disabled = !hasStock || isBusy;
          stickyAddBtn.classList.toggle('sold-out', !hasStock);
          stickyAddBtn.setAttribute('aria-disabled', String(!hasStock || isBusy));

          if (!isBusy) {
            stickyAddBtn.textContent = hasStock ? addToCartText : soldOutText;
          }

          if (stickyDecrease) {
            stickyDecrease.disabled = !hasStock || qty <= 1;
          }

          if (stickyIncrease) {
            stickyIncrease.disabled = !hasStock || qty >= maxQty;
          }

          return hasStock;
        };

        /*
          Si ya estaba enlazado, solo actualizamos el estado.
        */
        if (stickyForm.dataset.quickAddInventoryBound === 'true') {
          if (typeof stickyForm.quickAddStickyUpdateState === 'function') {
            stickyForm.quickAddStickyUpdateState();
          } else {
            updateStickyState();
          }

          return;
        }

        stickyForm.dataset.quickAddInventoryBound = 'true';
        stickyForm.quickAddStickyUpdateState = updateStickyState;

        /*
          CAMBIO 1:
          Función central para controlar cantidad del sticky dentro del quick add.
          Esto arregla los botones + y -.
        */
        const syncQuickAddStickyQuantity = (nextValue) => {
          const variant = getCurrentVariant();

          let hasStock = this.quickAddVariantHasStock(variant);

          if (hasStock === null) {
            hasStock = this.quickAddFallbackHasStock(root);
          }

          const maxQty = this.getQuickAddVariantMaxQuantity(variant);

          let qty = parseInt(nextValue, 10);

          if (Number.isNaN(qty)) {
            qty = 1;
          }

          qty = Math.max(1, Math.min(qty, maxQty));

          if (stickyQuantity) {
            stickyQuantity.value = String(qty);
            stickyQuantity.setAttribute('value', String(qty));
            stickyQuantity.max = String(maxQty);
            stickyQuantity.disabled = !hasStock;
          }

          if (stickyDecrease) {
            stickyDecrease.disabled = !hasStock || qty <= 1;
          }

          if (stickyIncrease) {
            stickyIncrease.disabled = !hasStock || qty >= maxQty;
          }

          updateStickyState();

          return qty;
        };

        /*
          Capturamos el submit del sticky dentro del quick add.
          Si está agotado, lo bloqueamos.
          Si tiene stock, sincronizamos variante y cantidad con el formulario principal.
        */
        stickyForm.addEventListener(
          'submit',
          (event) => {
            const hasStock = updateStickyState();

            event.preventDefault();
            event.stopImmediatePropagation();

            if (!hasStock) return;

            this.submitStickyThroughQuickAddMainForm(root, stickyForm, stickyAddBtn);
          },
          true
        );

        /*
          CAMBIO 2:
          Listeners reales para aumentar y disminuir cantidad.
        */
        if (stickyIncrease) {
          stickyIncrease.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (stickyIncrease.disabled) return;

            const current = parseInt(stickyQuantity?.value, 10) || 1;

            syncQuickAddStickyQuantity(current + 1);
          });
        }

        if (stickyDecrease) {
          stickyDecrease.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (stickyDecrease.disabled) return;

            const current = parseInt(stickyQuantity?.value, 10) || 1;

            syncQuickAddStickyQuantity(current - 1);
          });
        }

        if (stickyQuantity) {
          stickyQuantity.addEventListener('input', (event) => {
            syncQuickAddStickyQuantity(event.target.value);
          });

          stickyQuantity.addEventListener('change', (event) => {
            syncQuickAddStickyQuantity(event.target.value);
          });
        }

        const mainVariantInput = getMainVariantInput();

        if (mainVariantInput) {
          const observer = new MutationObserver(() => {
            updateStickyState();
          });

          observer.observe(mainVariantInput, {
            attributes: true,
            attributeFilter: ['value']
          });
        }

        updateStickyState();

        setTimeout(updateStickyState, 100);
        setTimeout(updateStickyState, 500);
      }

      readQuickAddStickyVariants(root) {
        /*
          Guardamos las variantes en el root para que, si actualizamos inventario
          localmente después de agregar, el cambio se mantenga en el modal.
        */
        if (Array.isArray(root.quickAddStickyVariants)) {
          return root.quickAddStickyVariants;
        }

        const variantsJson =
          root.querySelector('.sticky-product-variants-json') ||
          root.querySelector('#sticky-product-variants-json');

        if (!variantsJson) {
          root.quickAddStickyVariants = [];
          return root.quickAddStickyVariants;
        }

        try {
          const parsed = JSON.parse(variantsJson.textContent);

          root.quickAddStickyVariants = Array.isArray(parsed) ? parsed : [];

          return root.quickAddStickyVariants;
        } catch (error) {
          console.error('Error leyendo variantes del sticky en quick add:', error);

          root.quickAddStickyVariants = [];

          return root.quickAddStickyVariants;
        }
      }

      quickAddVariantHasStock(variant) {
        if (!variant) return null;

        const inventoryManaged =
          variant.inventory_management !== null &&
          variant.inventory_management !== undefined &&
          variant.inventory_management !== false &&
          variant.inventory_management !== '';

        const inventoryQuantity = Number(variant.inventory_quantity);

        if (!variant.available) return false;

        /*
          Aunque Shopify permita vender sin stock con inventory_policy: continue,
          aquí bloqueamos si inventory_quantity <= 0 porque necesitas validar saldo real.
        */
        if (inventoryManaged && !Number.isNaN(inventoryQuantity) && inventoryQuantity <= 0) {
          return false;
        }

        return true;
      }

      getQuickAddVariantMaxQuantity(variant) {
        if (!variant) return 99;

        const inventoryManaged =
          variant.inventory_management !== null &&
          variant.inventory_management !== undefined &&
          variant.inventory_management !== false &&
          variant.inventory_management !== '';

        const inventoryQuantity = Number(variant.inventory_quantity);

        if (inventoryManaged && !Number.isNaN(inventoryQuantity) && inventoryQuantity > 0) {
          return Math.min(inventoryQuantity, 99);
        }

        return 99;
      }

      quickAddFallbackHasStock(root) {
        const mainAddButton = this.getQuickAddMainAddButton(root);

        if (!mainAddButton) return true;

        const buttonText = mainAddButton.textContent.trim().toLowerCase();

        const looksSoldOut =
          buttonText.includes('sold out') ||
          buttonText.includes('agotado') ||
          buttonText.includes('sin stock') ||
          buttonText.includes('no disponible') ||
          buttonText.includes('unavailable');

        if (looksSoldOut) return false;
        if (mainAddButton.disabled) return false;
        if (mainAddButton.getAttribute('aria-disabled') === 'true') return false;

        return true;
      }

      getQuickAddMainProductForm(root) {
        return (
          Array.from(root.querySelectorAll('form[action*="/cart/add"]')).find((form) => {
            return !form.classList.contains('sticky-form') && !form.closest('.sticky-form');
          }) || null
        );
      }

      getQuickAddMainAddButton(root) {
        return (
          Array.from(
            root.querySelectorAll(
              'button[name="add"], product-form button[type="submit"], .product-form__submit'
            )
          ).find((button) => {
            return !button.closest('.sticky-form');
          }) || null
        );
      }

      submitStickyThroughQuickAddMainForm(root, stickyForm, stickyAddBtn) {
        const mainProductForm = this.getQuickAddMainProductForm(root);
        const mainAddButton = this.getQuickAddMainAddButton(root);

        if (!mainProductForm || !mainAddButton) {
          this.submitStickyFormByFetch(root, stickyForm, stickyAddBtn);
          return;
        }

        const stickyVariantInput = stickyForm.querySelector('input[name="id"]');

        const stickyQuantityInput =
          stickyForm.querySelector('input[name="quantity"]') ||
          stickyForm.querySelector('.sticky-quantity-input');

        const mainVariantInput = mainProductForm.querySelector('input[name="id"]');

        /*
          IMPORTANTE:
          Muchos quick add no traen input quantity.
          Si no existe, lo creamos oculto para que Shopify reciba la cantidad del sticky.
        */
        let mainQuantityInput = mainProductForm.querySelector('input[name="quantity"]');

        if (!mainQuantityInput && stickyQuantityInput) {
          mainQuantityInput = document.createElement('input');
          mainQuantityInput.type = 'hidden';
          mainQuantityInput.name = 'quantity';
          mainProductForm.appendChild(mainQuantityInput);
        }

        if (mainVariantInput && stickyVariantInput && stickyVariantInput.value) {
          mainVariantInput.value = stickyVariantInput.value;
          mainVariantInput.setAttribute('value', stickyVariantInput.value);

          mainVariantInput.dispatchEvent(
            new Event('input', {
              bubbles: true
            })
          );

          mainVariantInput.dispatchEvent(
            new Event('change', {
              bubbles: true
            })
          );
        }

        if (mainQuantityInput && stickyQuantityInput) {
          const stickyQty = stickyQuantityInput.value || '1';

          mainQuantityInput.value = stickyQty;
          mainQuantityInput.setAttribute('value', stickyQty);

          mainQuantityInput.dispatchEvent(
            new Event('input', {
              bubbles: true
            })
          );

          mainQuantityInput.dispatchEvent(
            new Event('change', {
              bubbles: true
            })
          );
        }

        if (mainAddButton.disabled || mainAddButton.getAttribute('aria-disabled') === 'true') {
          stickyAddBtn.textContent =
            stickyAddBtn.dataset.soldOutText ||
            stickyAddBtn.getAttribute('data-sold-out-text') ||
            'Sold out';

          stickyAddBtn.disabled = true;
          stickyAddBtn.classList.add('sold-out');
          stickyAddBtn.setAttribute('aria-disabled', 'true');

          return;
        }

        const originalText =
          stickyAddBtn.dataset.addText ||
          stickyAddBtn.getAttribute('data-add-text') ||
          stickyAddBtn.textContent.trim() ||
          'Add to cart';

        stickyAddBtn.classList.add('loading');
        stickyAddBtn.textContent = 'Adding...';
        stickyAddBtn.disabled = true;
        stickyAddBtn.setAttribute('aria-disabled', 'true');

        mainAddButton.click();

        /*
          Actualizamos inventario local en el modal para que, si el usuario agregó
          la última unidad disponible, el sticky pase a Sold out sin esperar recarga.
        */
        const variants = this.readQuickAddStickyVariants(root);
        const currentVariant = variants.find((variant) => {
          return String(variant.id) === String(stickyVariantInput?.value);
        });

        const addedQty = parseInt(stickyQuantityInput?.value, 10) || 1;

        if (currentVariant && currentVariant.inventory_management) {
          const currentInventory = Number(currentVariant.inventory_quantity);

          if (!Number.isNaN(currentInventory)) {
            currentVariant.inventory_quantity = currentInventory - addedQty;

            if (currentVariant.inventory_quantity <= 0) {
              currentVariant.available = false;
            }
          }
        }

        setTimeout(() => {
          stickyAddBtn.classList.remove('loading');
          stickyAddBtn.textContent = originalText;
          stickyAddBtn.disabled = false;
          stickyAddBtn.removeAttribute('aria-disabled');

          if (typeof stickyForm.quickAddStickyUpdateState === 'function') {
            stickyForm.quickAddStickyUpdateState();
          }
        }, 1500);
      }

      async submitStickyFormByFetch(root, stickyForm, stickyAddBtn) {
        const originalText =
          stickyAddBtn.dataset.addText ||
          stickyAddBtn.getAttribute('data-add-text') ||
          stickyAddBtn.textContent.trim() ||
          'Add to cart';

        const soldOutText =
          stickyAddBtn.dataset.soldOutText ||
          stickyAddBtn.getAttribute('data-sold-out-text') ||
          'Sold out';

        stickyAddBtn.classList.add('loading');
        stickyAddBtn.textContent = 'Adding...';
        stickyAddBtn.disabled = true;
        stickyAddBtn.setAttribute('aria-disabled', 'true');

        try {
          const formData = new FormData(stickyForm);
          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity'), 10) || 1;

          if (!variantId) {
            throw new Error('No se pudo obtener el ID de la variante.');
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [
                {
                  id: parseInt(variantId, 10),
                  quantity
                }
              ]
            })
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.message || result.description || 'Error adding product to cart');
          }

          const variants = this.readQuickAddStickyVariants(root);
          const currentVariant = variants.find((variant) => {
            return String(variant.id) === String(variantId);
          });

          if (currentVariant && currentVariant.inventory_management) {
            const currentInventory = Number(currentVariant.inventory_quantity);

            if (!Number.isNaN(currentInventory)) {
              currentVariant.inventory_quantity = currentInventory - quantity;

              if (currentVariant.inventory_quantity <= 0) {
                currentVariant.available = false;
              }
            }
          }

          stickyAddBtn.classList.remove('loading');
          stickyAddBtn.classList.add('success');
          stickyAddBtn.textContent = '✓ Agregado';

          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: {
                source: 'quick-add-sticky'
              }
            })
          );

          const cartDrawer = document.querySelector('cart-drawer');

          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }

          setTimeout(() => {
            stickyAddBtn.classList.remove('success');
            stickyAddBtn.textContent = originalText;
            stickyAddBtn.disabled = false;
            stickyAddBtn.removeAttribute('aria-disabled');

            if (typeof stickyForm.quickAddStickyUpdateState === 'function') {
              stickyForm.quickAddStickyUpdateState();
            }
          }, 1500);
        } catch (error) {
          console.error('Error agregando desde sticky quick add:', error);

          const errorText = String(error.message || '').toLowerCase();

          const isSoldOutError =
            errorText.includes('sold out') ||
            errorText.includes('agotado') ||
            errorText.includes('inventory') ||
            errorText.includes('not enough') ||
            errorText.includes('sin stock');

          stickyAddBtn.classList.remove('loading');

          if (isSoldOutError) {
            stickyAddBtn.textContent = soldOutText;
            stickyAddBtn.disabled = true;
            stickyAddBtn.classList.add('sold-out');
            stickyAddBtn.setAttribute('aria-disabled', 'true');

            return;
          }

          stickyAddBtn.textContent = 'Error - Intenta de nuevo';
          stickyAddBtn.style.background = '#dc3545';

          setTimeout(() => {
            stickyAddBtn.textContent = originalText;
            stickyAddBtn.style.background = '';
            stickyAddBtn.disabled = false;
            stickyAddBtn.removeAttribute('aria-disabled');
          }, 3000);
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });

        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        const richTextFields = productElement.querySelectorAll(
          '[class*="metafield-rich_text_field"], .metafield-rich_text_field'
        );

        if (richTextFields) richTextFields.forEach((field) => field.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;

        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);

        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(
              attribute.name,
              attribute.value.replace(oldId, newId)
            );
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');

        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');

        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');

        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');

        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}